<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { WalletPackage, WalletPackageContent } from '@/shared/interfaces/wallet';

const props = defineProps({
	quickPackages: { type: Array as () => WalletPackage[], required: true },
	variant: { type: String, default: 'dark' },
});

const emit = defineEmits(['onQuickTopUp']);
const { t } = useI18n();

const packages = computed(() => {
	if (!props.quickPackages?.length) return [];

	return props.quickPackages.slice(0, 2).map((pkg) => {
		const contents = pkg.contents.map((content: WalletPackageContent) => ({
			id: content.id,
			amount: content.amount,
			currency: content.currency,
			euroPrice: +content.amount * +content.currency.exchange_rate,
			isEuro: content.currency.name.toUpperCase() === 'EURO',
		}));

		const allEuroContents = contents.every((c) => c.isEuro);
		const showTokens = !allEuroContents;

		return {
			...pkg,
			priceInclFee: +pkg.clientx_price_incl_fee,
			displayContents: contents,
			showTokens,
		};
	});
});

const handleCardClick = (pkg) => {
	emit('onQuickTopUp', pkg);
};
</script>

<template>
	<div class="quick-topup-wrapper" :class="{ 'quick-topup-wrapper--light': variant === 'light' }">
		<div v-if="packages.length > 0" class="quick-topup-title">{{ t('top_up.quick_topup_title') }}</div>
		<div class="quick-topup-list" :class="{ 'quick-topup-list--light': variant === 'light' }">
			<div
				v-for="pkg in packages"
				:key="pkg.id"
				class="quick-topup-card"
				@click="handleCardClick(pkg)">
				<span class="quick-topup-price">€{{ pkg.priceInclFee.toFixed(2) }}</span>
				<div v-if="pkg.showTokens" class="quick-topup-tokens">
					<template v-for="(content, index) in pkg.displayContents" :key="content.id">
						<span v-if="index > 0" class="quick-topup-token-divider">|</span>
						<span class="quick-topup-token">
							<span class="quick-topup-token-icon">{{ content.currency.prefix }}</span>
							<span class="quick-topup-token-amount">{{ content.amount }}</span>
						</span>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.quick-topup-wrapper {
	margin-top: 8px;
	text-align: center;
}

.quick-topup-title {
	font-family: 'sourcesanspro-bold', sans-serif;
	font-size: 13px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: #111827;
	background: #ffffff;
	border-radius: 4px;
	padding: 4px 8px;
	margin-bottom: 6px;
	width: fit-content;
	display: inline-block;
}

.quick-topup-wrapper--light .quick-topup-title {
	color: #111827;
	background: #ffffff;
}

.quick-topup-list {
	display: flex;
	flex-direction: row;
	gap: 8px;
	margin-top: 8px;
	overflow-x: auto;
	scrollbar-width: none;
	-ms-overflow-style: none;
	justify-content: center;

	&::-webkit-scrollbar {
		display: none;
	}
}

.quick-topup-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	flex: 1 1 0;
	min-width: 100px;
	padding: 12px 16px;
	border-radius: 12px;
	background: #ffffff;
	border: 1px solid rgba(0, 0, 0, 0.08);
	cursor: pointer;
	transition: all 0.2s ease;

	&:active {
		transform: scale(0.98);
		background: #f0f0f0;
	}
}

.quick-topup-price {
	font-family: 'sourcesanspro-bold', sans-serif;
	font-size: 18px;
	color: #111827;
}

.quick-topup-tokens {
	display: flex;
	gap: 6px;
	margin-top: 4px;
	align-items: center;
	justify-content: center;
}

.quick-topup-token {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	font-family: 'sourcesanspro-regular', sans-serif;
	font-size: 12px;
	color: #374151;
}

.quick-topup-token-icon {
	font-size: 16px;
	margin-bottom: 1px;
}

.quick-topup-token-amount {
	font-size: 11px;
}

.quick-topup-list--light .quick-topup-card {
	background: #ffffff;
	border-color: rgba(0, 0, 0, 0.08);

	&:active {
		background: #f0f0f0;
	}
}

.quick-topup-list--light .quick-topup-card .quick-topup-price {
	color: #111827;
}

.quick-topup-list--light .quick-topup-token {
	color: #374151;
}

.quick-topup-list--light .quick-topup-token-divider {
	color: rgba(0, 0, 0, 0.2);
}
</style>
